// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useOperatorParty } from '@hooks/queries/utility-operator/useOperatorParty';
import { useCallback, useState } from 'react';
import { Typography } from '@mui/material';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useModuleParty } from '../../../contexts/ModuleContext';
import useRequestInstrumentSecurityControlService from '../../../hooks/mutations/registry/onboarding/useRequestInstrumentSecurityControlService';
import useMutate from '../../../hooks/mutations/useMutate';
import {
  ERR_CREATE_INSTRUMENT_SECURITY_CONTROL_SERVICE_REQUEST,
  SUC_CREATE_INSTRUMENT_SECURITY_CONTROL_SERVICE_REQUEST,
} from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestInstrumentSecurityControlServiceDialog = ({ open, onClose }: DialogProps) => {
  const [isLoading, setLoading] = useState(false);
  const [upgrader, setUpgrader] = useState<string>('');

  const operator = useOperatorParty()?.data;
  const party = useModuleParty();
  const requestInstrumentSecurityControlService = useRequestInstrumentSecurityControlService();
  const mutate = useMutate();

  const resetState = useCallback(() => {
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!operator) return;
    setLoading(true);
    try {
      await mutate(
        requestInstrumentSecurityControlService,
        { operator, instrumentSecurityControl: party, upgrader },
        SUC_CREATE_INSTRUMENT_SECURITY_CONTROL_SERVICE_REQUEST,
        ERR_CREATE_INSTRUMENT_SECURITY_CONTROL_SERVICE_REQUEST,
      );
    } finally {
      handleClose();
      setLoading(false);
    }
  }, [operator, party, upgrader, handleClose, mutate, requestInstrumentSecurityControlService]);

  const disableSubmit = !operator || isLoading || !upgrader;

  return (
    <FormDialog
      open={open}
      title="Request Instrument Security Control Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <Typography variant="body2">
        This will initiate an instrument security control service request for party{' '}
        <strong
          style={{
            display: 'inline-block',
            marginBlock: '0.5rem',
            overflowWrap: 'anywhere',
          }}
        >
          {party}
        </strong>{' '}
        from operator{' '}
        <strong
          style={{ display: 'inline-block', marginBlock: '0.5rem', overflowWrap: 'anywhere' }}
        >
          {operator}
        </strong>
        .
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <TextInput
          enableTopLabel
          label="Upgrader Party ID"
          value={upgrader}
          setValue={setUpgrader}
        />
      </Box>
    </FormDialog>
  );
};

export default RequestInstrumentSecurityControlServiceDialog;
